import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';
import { filter, finalize, map, noop, Observable, Subscription, tap } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Dashboard } from 'app/modules/dashboard/dashboard.types';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import { DashboardAddComponent } from 'app/modules/dashboard/add/add.component';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '@shared/components/widgets/dialog/confirm/confirm.component';
import { Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import { DuplicateDashboard } from '../state/actions';
import { DashboardState } from '../state/state';
import { FeatureFlag, NewPermission, Permission } from '@core/enums';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';

@Component({
  selector: 'dashboard-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardListComponent extends OnDestroyMixin implements OnInit, AfterViewChecked {
  public dataSource: MatTableDataSource<Dashboard>;
  public dashboardsCount: number;
  public dashboardsTableColumns: string[];
  NewPermission = NewPermission;
  protected readonly Permission = Permission;

  @Select(DashboardState.getDashboardById) selectedDashboard$: Observable<Dashboard>;
  protected readonly newPermissionsEnabled$ = this.store.select(
    AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
  );

  private sortApplied: boolean = false;

  @ViewChild(MatSort) private sort: MatSort;

  constructor(
    private readonly matDialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly dashboardService: DashboardService,
    private readonly confirmService: ConfirmService,
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    super();
    this.dashboardsTableColumns = ['name', 'created_by', 'created_at', 'actions'];
  }

  ngOnInit(): void {
    this.fetchDashboards();

    this.actions$
      .pipe(
        ofActionCompleted(DuplicateDashboard),
        tap(() => {
          const dashboard = this.store.selectSnapshot(DashboardState.getDashboardById);
          this.router.navigate(['dashboard', dashboard.id]);
        }),
      )
      .subscribe(noop);
  }

  ngAfterViewChecked(): void {
    this.applySortToDataSource();
  }

  rowClicked(dashboard: Dashboard): void {
    this.router.navigate(['/dashboard', dashboard.id]);
  }

  private fetchDashboards(): Subscription {
    return this.dashboardService
      .getDashboards()
      .pipe(
        untilComponentDestroyed(this),
        map((dashboards: Dashboard[]) => {
          this.dataSource = new MatTableDataSource<Dashboard>(dashboards);
          this.dashboardsCount = dashboards.length;
        }),
        finalize(() => {
          this.cdRef.detectChanges();
        }),
      )
      .subscribe(() => this.applySortToDataSource());
  }

  public applySortToDataSource(): void {
    if (this.dataSource && this.sort && !this.sortApplied) {
      this.dataSource.sort = this.sort;
      this.sortApplied = true;
    }
  }

  public openCreateDashboardDialog(): void {
    this.matDialog.open(DashboardAddComponent, {
      panelClass: 'mailbox-compose-dialog',
    });
  }

  public deleteDashboard(dashboard: Dashboard) {
    this.confirmService
      .openConfirmationModal({ message: `Are you sure you want to delete the ${dashboard.name} dashboard?` })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this.dashboardService
          .deleteDashboard(dashboard.id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            this.snackBar.open(`Succesfully deleted ${dashboard.name} dashboard`, '', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
            });
            this.sortApplied = false;
            this.fetchDashboards();
          });
      });

    return;
  }

  public trackByFn(index: number, item: Dashboard): number {
    return item.id || index;
  }

  duplicate(model: Dashboard): void {
    this.matDialog
      .open(ConfirmDialogComponent, {
        autoFocus: false,
        data: {
          title: 'Confirmation',
          message: `Are you sure you want to duplicate dashboard "${model.name}"?`,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        tap(() => {
          this.store.dispatch(new DuplicateDashboard(model.id));
        }),
      )
      .subscribe(noop);
  }

  isAllowedToEdit(dashboard: Dashboard): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.DASHBOARDS_EDIT));
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.DASHBOARDS_CREATE_EDIT));
  }

  canShowThreeDotMenu(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return (
        this.isAllowedToEdit(null) ||
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.DASHBOARDS_CREATE)) ||
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.DASHBOARDS_DELETE))
      );
    }

    return (
      this.store.selectSnapshot(UserState.hasPermission(Permission.DASHBOARDS_CREATE_EDIT)) ||
      this.store.selectSnapshot(UserState.hasPermission(Permission.DASHBOARDS_DELETE))
    );
  }
}
